import React, { useMemo, useState } from "react";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import PageLoader from "components/ui/PageLoader";
import SearchSelect from "components/ui/SearchSelect";
import GooglePlacesAutocomplete from "components/GooglePlacesAutoComplete";
import StateChecklist from "components/addressChange/StateChecklist";
import { showToast } from "components/ui/toast";
import { useAuthSchedulesQuery } from "apis/authSchedule";
import {
  usePreviewAddressChangeMutation,
  useApplyAddressChangeMutation,
} from "apis/addressChange";

const Step = ({ n, title, hint, children }) => (
  <div className="flex gap-4">
    <span className="shrink-0 w-7 h-7 rounded-full bg-black text-white text-sm font-medium flex items-center justify-center">
      {n}
    </span>
    <div className="flex-1 space-y-2">
      <div>
        <h2 className="font-medium text-black">{title}</h2>
        {hint && <p className="text-xs text-darkgray">{hint}</p>}
      </div>
      {children}
    </div>
  </div>
);

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded border border-mediumgray p-6 ${className}`}>
    {children}
  </div>
);

const AddressChange = () => {
  const [propertyId, setPropertyId] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [isManual, setIsManual] = useState(false);
  const [preview, setPreview] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [result, setResult] = useState(null);

  // Old address comes from the auth schedules — a property that never reached
  // an agreement isn't something anyone renames.
  const { data: schedules = [], isPending: loadingProps } =
    useAuthSchedulesQuery("address,propertyId");

  const propertyOptions = useMemo(
    () =>
      schedules
        .filter((s) => s.address && s.propertyId)
        .map((s) => ({
          value: s.propertyId,
          label: s.address,
          subLabel: [s.organizationName, s.isSettled ? "Settled" : "Active"]
            .filter(Boolean)
            .join(" · "),
        })),
    [schedules],
  );

  const selectedProperty =
    propertyOptions.find((o) => o.value === propertyId) || null;

  const clearPreview = () => {
    setPreview(null);
    setResult(null);
    setShowDetail(false);
  };

  const { mutate: runPreview, isPending: isPreviewing } =
    usePreviewAddressChangeMutation({
      onSuccess: (data) => {
        setPreview(data);
        setResult(null);
      },
      onError: (error) =>
        showToast(
          "error",
          error?.response?.data?.message || "Failed to check the new address",
        ),
    });

  const { mutate: runApply, isPending: isApplying } =
    useApplyAddressChangeMutation({
      onSuccess: (data) => {
        setResult(data);
        setPreview(null);
        setIsConfirmOpen(false);
        showToast("success", "Address updated");
      },
      onError: (error) => {
        setIsConfirmOpen(false);
        showToast(
          "error",
          error?.response?.data?.message || "Failed to update the address",
        );
      },
    });

  const reset = () => {
    setPropertyId("");
    setNewAddress("");
    setIsManual(false);
    clearPreview();
  };

  const handleCheck = () => {
    if (!propertyId) return showToast("error", "Select the current address");
    if (!newAddress.trim()) return showToast("error", "Enter the new address");
    runPreview({ propertyId, newAddress });
  };

  const handleApply = () =>
    runApply({
      propertyId,
      newAddress: preview.newAddress,
      deletePropertyIds: preview.conflicts.map((c) => c._id),
    });

  if (loadingProps) return <PageLoader />;

  const conflicts = preview?.conflicts || [];
  const deletedRows = conflicts.reduce((n, c) => n + (c.linked?.total || 0), 0);
  const danger =
    "bg-red-600 text-white rounded text-sm px-5 py-2.5 hover:opacity-80";

  return (
    <div className="container mx-auto pb-16 max-w-4xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-black">
            Change Property Address
          </h1>
          <p className="text-sm text-darkgray mt-1">
            Use this when the contract comes back with a different address.
            Beleef updates the address everywhere it is stored for that one
            property.
          </p>
        </div>
        {(preview || result) && (
          <button className="gray-button shrink-0" onClick={reset}>
            Start over
          </button>
        )}
      </div>

      <Card className="space-y-8">
        <Step
          n="1"
          title="Which property are you fixing?"
          hint="Search by the address Beleef has today."
        >
          <SearchSelect
            options={propertyOptions}
            value={selectedProperty}
            onChange={(opt) => {
              setPropertyId(opt?.value || "");
              clearPreview();
            }}
            placeholder="Search the current address"
            isClearable
          />
          {selectedProperty?.subLabel && (
            <p className="text-xs text-darkgray">{selectedProperty.subLabel}</p>
          )}
        </Step>

        <Step
          n="2"
          title="What is the correct address?"
          hint="Type it exactly as it appears on the contract."
        >
          {isManual ? (
            <input
              type="text"
              value={newAddress}
              onChange={(e) => {
                setNewAddress(e.target.value);
                clearPreview();
              }}
              placeholder="e.g. Lot 12 Smith Road, Kogarah"
              className="w-full p-2 border border-mediumgray rounded outline-none min-h-[40px] text-sm"
            />
          ) : (
            <GooglePlacesAutocomplete
              address={newAddress}
              setAddress={(value) => {
                setNewAddress(value);
                clearPreview();
              }}
              onSelectAddress={(shortAddress) => setNewAddress(shortAddress)}
              placeholder="Start typing the new address..."
            />
          )}

          <button
            type="button"
            className="text-xs text-black underline"
            onClick={() => {
              setIsManual((v) => !v);
              clearPreview();
            }}
          >
            {isManual
              ? "Search for the address instead"
              : "Not in the list? Type it manually"}
          </button>

          <p className="text-xs text-darkgray">
            {isManual
              ? "Saved exactly as you type it. Street then suburb — no state or postcode."
              : "New sub-divisions and Lot addresses often aren't in the search."}
          </p>
        </Step>

        <div className="flex items-center justify-between pt-4 border-t border-mediumgray">
          <p className="text-xs text-darkgray">
            Nothing changes yet — the next screen shows exactly what will happen.
          </p>
          <Button
            className="black-button px-5 py-2.5"
            onClick={handleCheck}
            loading={isPreviewing}
          >
            Check this change
          </Button>
        </div>
      </Card>

      {isPreviewing && (
        <p className="text-sm text-darkgray mt-4 text-center">
          Checking every record for this property — this takes a few seconds.
        </p>
      )}

      {/* ── Review ── */}
      {preview && (
        <div className="mt-6 space-y-4">
          <Card>
            <p className="text-xs uppercase tracking-wide text-darkgray mb-2">
              You are changing
            </p>
            <p className="text-lg text-black leading-snug">
              <span className="line-through text-darkgray">
                {preview.property.address}
              </span>
            </p>
            <p className="text-lg font-semibold text-black leading-snug">
              {preview.newAddress}
            </p>
            {preview.property.agent && (
              <p className="text-xs text-darkgray mt-2">
                Agent: {preview.property.agent}
              </p>
            )}

            {/* The order matters and isn't obvious, so it is spelled out as
                numbered steps rather than implied by the word "first". */}
            <div className="mt-5 pt-5 border-t border-mediumgray">
              <p className="text-xs uppercase tracking-wide text-darkgray mb-3">
                What happens when you confirm
              </p>
              <ol className="space-y-3">
                {conflicts.length > 0 && (
                  <li className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-red-600 text-white text-xs font-medium flex items-center justify-center">
                      1
                    </span>
                    <p className="text-sm text-black">
                      The {conflicts.length} propert
                      {conflicts.length === 1 ? "y" : "ies"} already on{" "}
                      <strong>{preview.newAddress}</strong>{" "}
                      {conflicts.length === 1 ? "is" : "are"} deleted, along
                      with all {deletedRows} of their records.
                    </p>
                  </li>
                )}
                <li className="flex gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-black text-white text-xs font-medium flex items-center justify-center">
                    {conflicts.length > 0 ? "2" : "1"}
                  </span>
                  <p className="text-sm text-black">
                    This property is renamed to{" "}
                    <strong>{preview.newAddress}</strong> in{" "}
                    <strong>{preview.rename.totalValues}</strong> place
                    {preview.rename.totalValues === 1 ? "" : "s"}.{" "}
                    <button
                      className="underline text-darkgray"
                      onClick={() => setShowDetail((v) => !v)}
                    >
                      {showDetail ? "Hide detail" : "Show detail"}
                    </button>
                  </p>
                </li>
              </ol>

              {conflicts.length === 0 && (
                <p className="text-sm text-darkgray mt-3">
                  Nothing else is on the new address, so nothing is deleted.
                </p>
              )}
            </div>

            {showDetail && (
              <div className="mt-4 pt-4 border-t border-mediumgray flex flex-wrap gap-1">
                {preview.rename.collections.map((c) => (
                  <span
                    key={c.name}
                    className="text-[10px] px-2 py-0.5 rounded-full border border-mediumgray text-black"
                  >
                    {c.name} · {c.values}
                  </span>
                ))}
              </div>
            )}

          </Card>

          {conflicts.map((c) => (
            <Card key={c._id} className="border-red-300 bg-red-50">
              <p className="text-xs uppercase tracking-wide text-red-700 mb-2">
                Will be deleted permanently
              </p>
              <p className="text-base font-semibold text-black">{c.address}</p>
              <p className="text-xs text-darkgray mb-4">
                {c.agent || "No agent"} · added{" "}
                {new Date(c.createdAt).toLocaleDateString("en-AU")} ·{" "}
                {c.linked.total} record{c.linked.total === 1 ? "" : "s"}
              </p>
              <p className="text-sm text-black mb-2">
                Work already done on it that will be lost:
              </p>
              <StateChecklist state={c.state} tone="danger" />
            </Card>
          ))}

          <Card>
            <h2 className="font-medium text-black mb-1">
              Where this property is up to
            </h2>
            <p className="text-xs text-darkgray mb-4">
              For context — none of this is deleted, it is only renamed.
            </p>
            <StateChecklist state={preview.state} />
          </Card>

          {preview.warnings.length > 0 && (
            <Card className="bg-yellow-50 border-yellow-300">
              <h2 className="font-medium text-black mb-1">
                Beleef cannot fix these for you
              </h2>
              <p className="text-xs text-darkgray mb-3">
                Handle each one outside Beleef after the change.
              </p>
              <ul className="space-y-2">
                {preview.warnings.map((w) => (
                  <li key={w} className="text-sm text-black flex gap-2">
                    <span className="shrink-0">•</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button className="gray-button" onClick={reset}>
              Cancel
            </button>
            <Button
              className={conflicts.length ? danger : "black-button px-5 py-2.5"}
              onClick={() => setIsConfirmOpen(true)}
            >
              {conflicts.length
                ? `Delete ${conflicts.length} propert${
                    conflicts.length === 1 ? "y" : "ies"
                  } and change address`
                : "Change the address"}
            </Button>
          </div>
        </div>
      )}

      {/* ── Result ── */}
      {result && (
        <Card className="mt-6">
          <p className="text-xs uppercase tracking-wide text-darkgray mb-2">
            Done
          </p>
          <p className="text-lg font-semibold text-black">
            {result.newAddress}
          </p>
          <p className="text-sm text-darkgray mt-1">
            was {result.oldAddress}
          </p>
          <p className="text-sm text-black mt-4">
            {result.totalRenamed} place
            {result.totalRenamed === 1 ? "" : "s"} updated
            {result.deleted.length
              ? `, ${result.deleted.length} duplicate propert${
                  result.deleted.length === 1 ? "y" : "ies"
                } deleted`
              : ""}
            .
          </p>
        </Card>
      )}

      <Modal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title="Are you sure?"
        className="max-w-lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-black">
            <strong>{preview?.property.address}</strong> becomes{" "}
            <strong>{preview?.newAddress}</strong> in{" "}
            {preview?.rename.totalValues} place
            {preview?.rename.totalValues === 1 ? "" : "s"}.
          </p>

          {conflicts.length > 0 && (
            <div className="bg-red-50 border border-red-300 rounded p-4">
              <p className="text-sm text-black font-medium mb-2">
                First, {conflicts.length} propert
                {conflicts.length === 1 ? "y" : "ies"} on{" "}
                {preview?.newAddress} will be deleted — {deletedRows} record
                {deletedRows === 1 ? "" : "s"} in total:
              </p>
              <ul className="text-sm text-black list-disc pl-4 space-y-1">
                {conflicts.map((c) => (
                  <li key={c._id}>
                    {c.address}
                    <span className="text-darkgray">
                      {" — "}
                      {c.state
                        .filter((s) => s.done)
                        .map((s) => s.label)
                        .join(", ") || "nothing recorded"}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-red-700 font-medium mt-3">
                This cannot be undone.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-mediumgray">
            <button
              className="gray-button"
              onClick={() => setIsConfirmOpen(false)}
            >
              Go back
            </button>
            <Button
              className={conflicts.length ? danger : "black-button px-5 py-2.5"}
              onClick={handleApply}
              loading={isApplying}
            >
              {conflicts.length ? "Delete and change" : "Yes, change it"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddressChange;
